import DateRangePicker from '@wojtekmaj/react-daterange-picker';
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import 'react-calendar/dist/Calendar.css';
import DataTable from 'react-data-table-component';
import IconCalendar from '../../assets/SidebarIcons/icon-calendar-time.svg';
import { getRequest } from '../../helpers/apiHandler';
import AutoHeight from '../AutoHeight';

import './_smsTransaction.scss';
import { sortByDate } from '../../helpers/helperFunction';

const white_label_perkeso = process.env.REACT_APP_WHITE_LABEL_PERKESO === 'true';

const SMSreportModal = ({ show, onClose, onExited, access_token, user_id, date, setDate }) => {
  const [loadingData, setLoadingData] = useState(false);
  const [loadingDownload, setLoadingDownload] = useState(false);
  const [data, setData] = useState([]);
  const [columns, setColumns] = useState([]);
  // total_sent sums message parts, matching the SMS Transaction Report table
  const [totals, setTotals] = useState({ total_sent: 0, total_records: 0 });

  const [alertState, setAlertState] = useState({
    show: false,
    variant: 'danger',
    message: '',
  });

  const perkesoHeaders = () => [
    'date',
    'transaction_id',
    'message',
    'transaction_count',
    'country',
    'phone_number',
    'status',
    'user_id',
    'name',
    'email',
    'updated_at',
    'source',
  ];

  const elfoHeaders = () => [
    'date',
    'transaction_id',
    'message',
    'transaction_count',
    'country',
    'phone_number',
    'cost',
    'status',
    'user_id',
    'name',
    'email',
    'updated_at',
    'source',
  ];

  const generateCSV = async () => {
    setLoadingDownload(true);
    try {
      // Delay to make the loadingDownload state sets in
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Perkeso's DB have timezone that is different from Elfo's
      // and it's more efficient to do this in the frontend since they were taking the same column to take the date for
      const formattedData = data.map((row) => ({
        ...row,
        date: white_label_perkeso
          ? row.date
          : moment(row.updated_at).format('DD/MM/YYYY hh:mm:ss A'),
        updated_at: white_label_perkeso
          ? row.date
          : moment(row.updated_at).format('DD/MM/YYYY hh:mm:ss A'),
      }));

      const headers = white_label_perkeso ? perkesoHeaders() : elfoHeaders();
      const headerRow = headers.map((header) => `"${header}"`).join(',');

      const rows = formattedData.map((row) =>
        headers
          .map((header) => {
            const value = (row[header] || '')
              .toString()
              .replace(/[\r\n\t\f\v\s]+/g, ' ')
              .trim();
            return `"${value.replace(/"/g, '""')}"`;
          })
          .join(',')
      );

      const totalRow = headers
        .map((header) => {
          if (header === 'date') return '"Total SMS Sent"';
          if (header === 'transaction_count') return `"${totals.total_sent}"`;
          return '""';
        })
        .join(',');

      const csvContent = '\ufeff' + headerRow + '\n' + rows.join('\n') + '\n' + totalRow;
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `SMS-Report-${moment().format('YYYY-MM-DD')}.csv`;

      document.body.appendChild(link);
      link.click();

      // Wait a bit before cleanup
      await new Promise((resolve) => setTimeout(resolve, 100));

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setAlertState({
        message: 'Your SMS report has been successfully exported to CSV.',
        show: true,
        variant: 'success',
      });
    } catch (error) {
      console.error('CSV generation error:', error);
      setAlertState({
        message: `Export failed: ${error.message || 'Unknown error occurred'}`,
        show: true,
        variant: 'danger',
      });
    } finally {
      // Add a small delay before removing loading state
      await new Promise((resolve) => setTimeout(resolve, 500));
      setLoadingDownload(false);
    }
  };

  const getSMSReportData = () => {
    setLoadingData(true);
    const payload = {
      headers: { Authorization: access_token.token },
      params: {
        start_date: date[0] ? moment(date[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss.SSSSSS') : null,
        end_date: date[1] ? moment(date[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss.SSSSSS') : null,
        user_id: user_id || null,
      },
    };

    getRequest(`/admin/sms-report`, payload, (res) => {
      if (res.success) {
        const rows = res.data.sms_data || [];
        setData(rows);
        setTotals({
          total_sent: Number(rows[0]?.total_sent || 0),
          total_records: Number(res.data.total_records || 0),
        });
      } else {
        setAlertState({
          message: res.message,
          show: true,
          variant: 'danger',
        });
      }
      setLoadingData(false);
    });
  };

  const generateColumns = () => {
    return [
      { name: 'Date', sortable: true, selector: (row) => row.date, sortFunction: sortByDate('date'), minWidth: '120px' },
      { name: 'Trx ID', sortable: true, selector: (row) => row.transaction_id, grow: 2, wrap: true },
      { name: 'Message', sortable: true, selector: (row) => row.message, grow: 3, wrap: true },
      { name: 'Trx Count', sortable: true, selector: (row) => row.transaction_count, width: '100px' },
      { name: 'Country', sortable: true, selector: (row) => row.country, width: '95px' },
      { name: 'Phone', sortable: true, selector: (row) => row.phone_number, minWidth: '130px' },
      ...(!white_label_perkeso
        ? [{ name: 'Cost', sortable: true, selector: (row) => row.cost, width: '80px' }]
        : []),
      { name: 'Status', sortable: true, selector: (row) => row.status, width: '105px' },
      { name: 'Source', sortable: true, selector: (row) => row.source, minWidth: '120px', wrap: true },
    ];
  };

  useEffect(() => {
    setColumns(generateColumns());
  }, []);

  useEffect(() => {
    if (show && user_id) {
      getSMSReportData();
    }
  }, [show, date, user_id]);

  // Reset only after the fade-out finishes, so the content doesn't collapse mid-animation.
  const handleExited = () => {
    setData([]);
    setTotals({ total_sent: 0, total_records: 0 });
    onExited();
  };

  return (
    <Modal show={show} onHide={onClose} onExited={handleExited} centered size="xl">
      <Modal.Header closeButton>
        <div className="w-100 d-flex justify-content-between align-items-center">
          <Modal.Title>SMS Report of User ID: {user_id}</Modal.Title>
          <div className="d-flex gap-2">
            <DateRangePicker
              value={date}
              onChange={setDate}
              clearIcon={null}
              calendarIcon={<img src={IconCalendar} alt="..." />}
              format="MM-dd-y"
              rangeDivider={<i className="fas fa-long-arrow-alt-right" />}
              dayPlaceholder="DD"
              monthPlaceholder="MM"
              yearPlaceholder="YYYY"
            />

            <Button onClick={generateCSV} disabled={loadingDownload || loadingData}>
              {loadingDownload ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        <div className="w-100" style={{ padding: 30 }}>
          <AutoHeight>
            {!loadingData && (
              <div className="mb-3 fw-bold">
                Total SMS Sent: {totals.total_sent.toLocaleString()} ({totals.total_records.toLocaleString()} messages)
              </div>
            )}
            <DataTable
              columns={columns}
              data={data}
              progressPending={loadingData}
              progressComponent={<Spinner animation="border" variant="primary" className="my-5" />}
              fixedHeader
              fixedHeaderScrollHeight="55vh"
              pagination
              paginationComponentOptions={{
                rowsPerPageText: 'Items per page:',
              }}
            />
          </AutoHeight>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default SMSreportModal;
